import { useState } from 'react';
import type { SearchChipAdminItem } from '@bailafm/shared';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { ApiClientError } from '@/lib/api';

interface ChipDeleteDialogProps {
  chip: SearchChipAdminItem | null;
  onClose(): void;
  onConfirm(chip: SearchChipAdminItem): Promise<void>;
}

export function ChipDeleteDialog({ chip, onClose, onConfirm }: ChipDeleteDialogProps) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    if (!chip) return;
    setPending(true);
    setError(null);
    try {
      await onConfirm(chip);
      toast.success('Chip eliminado');
      onClose();
    } catch (err) {
      const message =
        err instanceof ApiClientError && err.code === 'etag_mismatch'
          ? 'Otro usuario modificó este chip. Recarga la lista y vuelve a intentarlo.'
          : err instanceof ApiClientError
            ? err.message
            : 'No se pudo eliminar el chip.';
      setError(message);
      toast.error(message);
    } finally {
      setPending(false);
    }
  }

  const preview = chip
    ? chip.label.es ?? chip.label.ca ?? chip.label.en ?? chip.id
    : '';

  return (
    <AlertDialog
      open={Boolean(chip)}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Eliminar chip</AlertDialogTitle>
          <AlertDialogDescription>
            ¿Quieres eliminar el chip <strong>{preview}</strong>? Esta acción no
            se puede deshacer.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              void handleConfirm();
            }}
            disabled={pending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {pending ? 'Eliminando…' : 'Eliminar'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
